<template>
  <div class="historico-filter-container bg-[var(--fundoForm)] p-4 rounded-lg shadow-lg">
    <!-- Filter Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-[var(--text)] font-semibold">Filtros do Histórico</h3>
      <button 
        @click="clearAllFilters" 
        class="text-blue-400 hover:text-blue-300 text-sm underline"
      >
        Limpar Filtros
      </button>
    </div>

    <!-- Performance Warning -->
    <div 
      v-if="performanceWarning.level !== 'none'" 
      :class="getWarningClasses()"
      class="mb-4 p-3 rounded-md flex items-center"
    >
      <i class="fas fa-exclamation-triangle mr-2"></i>
      <div class="flex-1">
        <div class="font-semibold">{{ performanceWarning.message }}</div>
        <div class="text-sm opacity-90">
          Estimativa: {{ performanceWarning.count }} registros
          <span v-if="performanceWarning.days"> • {{ performanceWarning.days }} dias</span>
        </div>
      </div>
    </div>

    <!-- Filter Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <!-- Date Range -->
      <div class="filter-group">
        <label class="filter-label">Data Início</label>
        <input
          type="date"
          v-model="filters.data_inicio"
          @change="onDateChange"
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label">Data Fim</label>
        <input
          type="date"
          v-model="filters.data_fim"
          @change="onDateChange"
          class="filter-input"
        />
      </div>

      <!-- Quick Date Presets -->
      <div class="filter-group">
        <label class="filter-label">Período Rápido</label>
        <select v-model="selectedPreset" @change="applyDatePreset" class="filter-input">
          <option value="">Personalizado</option>
          <option value="today">Hoje</option>
          <option value="yesterday">Ontem</option>
          <option value="week">Última Semana</option>
          <option value="month">Último Mês</option>
          <option value="quarter">Último Trimestre</option>
        </select>
      </div>

      <!-- Model Name -->
      <div class="filter-group">
        <label class="filter-label">Modelo</label>
        <select v-model="filters.model_name" @change="onFilterChange" class="filter-input">
          <option value="">Todos os Modelos</option>
          <option v-for="model in availableModels" :key="model" :value="model">
            {{ model }}
          </option>
        </select>
      </div>

      <!-- Action -->
      <div class="filter-group">
        <label class="filter-label">Ação</label>
        <select v-model="filters.acao" @change="onFilterChange" class="filter-input">
          <option value="">Todas as Ações</option>
          <option v-for="action in availableActions" :key="action" :value="action">
            {{ action }}
          </option>
        </select>
      </div>

      <!-- User Name -->
      <div class="filter-group">
        <label class="filter-label">Utilizador</label>
        <input
          type="text"
          v-model="filters.utilizador_nome"
          @input="onFilterChange"
          placeholder="Nome do utilizador"
          class="filter-input"
        />
      </div>

      <!-- Object ID -->
      <div class="filter-group">
        <label class="filter-label">ID do Objeto</label>
        <input
          type="number"
          v-model="filters.objeto_id"
          @input="onFilterChange"
          placeholder="ID do objeto"
          class="filter-input"
        />
      </div>
    </div>

    <!-- Advanced Options Toggle -->
    <div class="mt-4">
      <button
        @click="showAdvanced = !showAdvanced"
        class="text-blue-400 hover:text-blue-300 text-sm flex items-center"
      >
        <i :class="showAdvanced ? 'fa-caret-up' : 'fa-caret-down'" class="fas mr-2"></i>
        Opções Avançadas
      </button>
    </div>

    <!-- Advanced Options -->
    <div v-if="showAdvanced" class="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div class="filter-group">
        <label class="filter-label">Endpoint (contém)</label>
        <input
          type="text"
          v-model="filters.endpoint"
          @input="onFilterChange"
          placeholder="Buscar em endpoint"
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label">Campo (contém)</label>
        <input
          type="text"
          v-model="filters.campo"
          @input="onFilterChange"
          placeholder="Nome do campo"
          class="filter-input"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label">Valor (contém)</label>
        <input
          type="text"
          v-model="filters.valor_novo"
          @input="onFilterChange"
          placeholder="Buscar em valores"
          class="filter-input"
        />
      </div>
    </div>

    <!-- Filter Summary -->
    <div v-if="hasActiveFilters" class="mt-4 p-3 bg-blue-600 bg-opacity-20 rounded-md">
      <div class="text-sm text-blue-300">
        <strong>Filtros Ativos:</strong>
        <span v-for="(summary, index) in getFilterSummary()" :key="index" class="ml-2">
          {{ summary }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from 'axios'

// Props
const props = defineProps({
  defaultModelName: {
    type: String,
    default: ''
  },
  defaultDateRange: {
    type: Number,
    default: 7
  },
  maxRecords: {
    type: Number,
    default: 1000
  },
  showPerformanceWarnings: {
    type: Boolean,
    default: true
  }
})

// Emits
const emit = defineEmits(['filters-changed', 'performance-warning'])

// Reactive data
const filters = ref({
  data_inicio: '',
  data_fim: '',
  model_name: props.defaultModelName,
  acao: '',
  utilizador_nome: '',
  objeto_id: '',
  endpoint: '',
  campo: '',
  valor_novo: ''
})

const selectedPreset = ref('')
const showAdvanced = ref(false)
const loading = ref(false)

// Metadata from API
const availableModels = ref([])
const availableActions = ref([])
const availableHttpMethods = ref([])
const performanceStats = ref({})

// Performance warning state
const performanceWarning = ref({
  level: 'none',
  message: '',
  count: 0,
  days: 0
})



// Computed
const hasActiveFilters = computed(() => {
  return Object.values(filters.value).some(value => value !== '' && value !== null && value !== undefined)
})

// Methods
const loadMetadata = async () => {
  try {
    const baseURL = process.env.VUE_APP_URL_BASE
    const response = await axios.get(`${baseURL}/api/v1/historico/metadata/`)
    console.log('Metadata response:', response.data)
    
    // Handle your API response structure: { row, status, message, data }
    if (response.data.status === 200 && response.data.data) {
      const metadata = response.data.data
      availableModels.value = metadata.model_names || []
      availableActions.value = metadata.actions || []
      availableHttpMethods.value = metadata.http_methods || []
      performanceStats.value = metadata.statistics || {}
    }
    
    
  } catch (error) {
    console.error('Error loading historico metadata:', error)
  }
}

const getDefaultDateRange = () => {
  const today = new Date()
  const startDate = new Date(today)
  startDate.setDate(today.getDate() - props.defaultDateRange)
  
  filters.value.data_inicio = startDate.toISOString().split('T')[0]
  filters.value.data_fim = today.toISOString().split('T')[0]
}

const applyDatePreset = () => {
  const today = new Date()
  let startDate = new Date(today)
  
  switch (selectedPreset.value) {
    case 'today':
      startDate = new Date(today)
      break
    case 'yesterday':
      startDate.setDate(today.getDate() - 1)
      filters.value.data_fim = startDate.toISOString().split('T')[0]
      break
    case 'week':
      startDate.setDate(today.getDate() - 7)
      break
    case 'month':
      startDate.setMonth(today.getMonth() - 1)
      break
    case 'quarter':
      startDate.setMonth(today.getMonth() - 3)
      break
    default:
      return
  }
  
  filters.value.data_inicio = startDate.toISOString().split('T')[0]
  if (selectedPreset.value !== 'yesterday') {
    filters.value.data_fim = today.toISOString().split('T')[0]
  }
  
  onDateChange()
}

const getDaysBetween = (start, end) => {
  if (!start || !end) return 0
  const startDate = new Date(start)
  const endDate = new Date(end)
  const diffTime = Math.abs(endDate - startDate)
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

const checkPerformance = async () => {
  if (!props.showPerformanceWarnings) return
  
  try {
    const queryParams = new URLSearchParams()
    Object.entries(filters.value).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value)
      }
    })
    
    const baseURL = process.env.VUE_APP_URL_BASE
    const response = await axios.get(`${baseURL}/api/v1/historico/estimate/?${queryParams}`)
    console.log('Performance response:', response.data)
    
    // Handle your API response structure: { row, status, message, data }
    if (response.data.status === 200 && response.data.data) {
      const estimate = response.data.data
      performanceWarning.value = {
        level: estimate.warning_level || 'none',
        message: estimate.warning_message || '',
        count: estimate.estimated_count || 0,
        days: getDaysBetween(filters.value.data_inicio, filters.value.data_fim)
      }
    } else {
      performanceWarning.value = {
        level: 'none',
        message: '',
        count: 0,
        days: 0
      }
    }
    
    emit('performance-warning', performanceWarning.value)
  } catch (error) {
    console.error('Error checking performance:', error)
    // Set default warning on error
    performanceWarning.value = {
      level: 'none',
      message: '',
      count: 0,
      days: 0
    }
  }
}

const onDateChange = () => {
  selectedPreset.value = '' // Clear preset when manual date change
  onFilterChange()
}

const onFilterChange = () => {
  // Debounce the performance check and filter emission
  clearTimeout(window.historicoFilterTimeout)
  window.historicoFilterTimeout = setTimeout(() => {
    checkPerformance()
    emit('filters-changed', { ...filters.value })
  }, 500)
}

const clearAllFilters = () => {
  filters.value = {
    data_inicio: '',
    data_fim: '',
    model_name: props.defaultModelName,
    acao: '',
    utilizador_nome: '',
    objeto_id: '',
    endpoint: '',
    campo: '',
    valor_novo: ''
  }
  selectedPreset.value = ''
  performanceWarning.value = { level: 'none', message: '', count: 0, days: 0 }
  
  // Set default date range
  getDefaultDateRange()
  emit('filters-changed', { ...filters.value })
}

const getFilterSummary = () => {
  const summary = []
  if (filters.value.model_name) summary.push(`Modelo: ${filters.value.model_name}`)
  if (filters.value.acao) summary.push(`Ação: ${filters.value.acao}`)
  if (filters.value.data_inicio && filters.value.data_fim) {
    const days = getDaysBetween(filters.value.data_inicio, filters.value.data_fim)
    summary.push(`Período: ${days} dias`)
  }
  if (filters.value.utilizador_nome) summary.push(`Utilizador: ${filters.value.utilizador_nome}`)
  return summary
}

const getWarningClasses = () => {
  const base = 'border-l-4'
  switch (performanceWarning.value.level) {
    case 'high':
      return `${base} border-red-500 bg-red-600 bg-opacity-20 text-red-200`
    case 'medium':
      return `${base} border-yellow-500 bg-yellow-600 bg-opacity-20 text-yellow-200`
    case 'low':
      return `${base} border-blue-500 bg-blue-600 bg-opacity-20 text-blue-200`
    default:
      return `${base} border-gray-500 bg-gray-600 bg-opacity-20 text-gray-200`
  }
}

// Lifecycle
onMounted(async () => {
  await loadMetadata()
  getDefaultDateRange()
  
  // Initial performance check
  if (props.showPerformanceWarnings) {
    setTimeout(checkPerformance, 100)
  }
  
  // Emit initial filters
  emit('filters-changed', { ...filters.value })
})

// Watch for prop changes
watch(() => props.defaultModelName, (newValue) => {
  if (newValue && !filters.value.model_name) {
    filters.value.model_name = newValue
    onFilterChange()
  }
})
</script>

<style scoped>
.filter-group {
  @apply flex flex-col space-y-1;
}

.filter-label {
  @apply text-sm font-medium text-gray-300;
}

.filter-input {
  @apply bg-[var(--fundoForm)] border border-gray-600 rounded px-3 py-2 text-[var(--text)] text-sm;
  @apply focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500;
}

.filter-input:focus {
  @apply bg-opacity-80;
}

.historico-filter-container {
  @apply border border-gray-700;
}
</style>